import React, { useState, useEffect } from 'react';
import { useDecisionContext } from '../context/DecisionContext';
import { AIModelFactory } from '../services/apiService';

// 定义主题类型
type Theme = 'light' | 'dark';

// 定义动态表单字段接口
interface FormField {
  key: string;
  label: string;
  type: 'text' | 'password' | 'select' | 'textarea';
  options?: {value: string, label: string}[];
  placeholder?: string;
  rows?: number;
}

// 自定义模型接口
interface CustomModel {
  value: string;
  label: string;
}

const SettingsPanel: React.FC = () => {
  const { apiSettings, updateApiSettings, error, clearError } = useDecisionContext();
  const [isOpen, setIsOpen] = useState(false);
  const [formValues, setFormValues] = useState({...apiSettings});
  const [adapterType, setAdapterType] = useState(apiSettings.adapterType || 'openai');
  const [formFields, setFormFields] = useState<FormField[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  
  // 自定义模型状态
  const [customModels, setCustomModels] = useState<CustomModel[]>(() => {
    // 从localStorage加载自定义模型
    const savedModels = localStorage.getItem('customModels');
    if (savedModels) {
      try {
        return JSON.parse(savedModels);
      } catch (e) {
        console.error('解析自定义模型失败:', e);
        return [];
      }
    }
    return [];
  });
  
  const [newModelName, setNewModelName] = useState('');
  
  // 添加主题相关状态
  const [theme, setTheme] = useState<Theme>(() => {
    // 初始化时尝试从localStorage获取主题设置
    const savedTheme = localStorage.getItem('theme') as Theme;
    return savedTheme || 'light';
  });

  // 根据适配器类型获取配置字段
  useEffect(() => {
    try {
      const adapter = AIModelFactory.createAdapter(adapterType, {apiKey: apiSettings.apiKey});
      const fields = adapter.getConfigFields();
      
      // 移除模型字段，将在后面自定义添加
      const baseFields = fields.filter(field => field.key !== 'model');
      
      const formFieldsData: FormField[] = [
        ...baseFields.map(field => ({
          ...field,
          type: field.type === 'password' ? 'password' as const : 'text' as const,
          placeholder: field.key === 'apiKey' ? 'API密钥' : 
                      field.key === 'baseUrl' ? 'API端点URL' : ''
        })),
        {
          key: 'model',
          label: '模型',
          type: 'select' as const,
          options: customModels,
        },
        {
          key: 'prompt',
          label: '系统提示词',
          type: 'textarea',
          rows: 6,
          placeholder: '输入系统提示词，用于引导AI生成结构化的决策分析'
        }
      ];
      
      setFormFields(formFieldsData);
    } catch (e) {
      console.error('获取适配器配置失败:', e);
    }
  }, [adapterType, apiSettings.apiKey, customModels]);

  // 主题变化时，更新body类和localStorage
  useEffect(() => {
    // 应用主题到body元素
    if (theme === 'dark') {
      document.body.classList.add('dark-theme');
    } else {
      document.body.classList.remove('dark-theme');
    }
    
    // 保存主题设置
    localStorage.setItem('theme', theme);
  }, [theme]);

  // 切换主题
  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  // 添加自定义模型
  const addCustomModel = () => {
    if (newModelName.trim()) {
      const modelValue = newModelName.trim();
      const newModel = {
        value: modelValue,
        label: modelValue
      };
      
      const updatedModels = [...customModels, newModel];
      setCustomModels(updatedModels);
      setNewModelName('');
      
      // 保存到localStorage
      localStorage.setItem('customModels', JSON.stringify(updatedModels));
      
      // 如果是第一个添加的模型，自动选择它
      if (customModels.length === 0) {
        setFormValues(prev => ({...prev, model: modelValue}));
        setIsDirty(true);
      }
    }
  };
  
  // 删除自定义模型
  const removeCustomModel = (modelValue: string) => {
    const updatedModels = customModels.filter(model => model.value !== modelValue);
    setCustomModels(updatedModels);
    
    // 保存到localStorage
    localStorage.setItem('customModels', JSON.stringify(updatedModels));
    
    // 如果删除的是当前选中的模型，重置选择
    if (formValues.model === modelValue && updatedModels.length > 0) {
      setFormValues(prev => ({...prev, model: updatedModels[0].value}));
      setIsDirty(true);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isDirty) {
      updateApiSettings(formValues);
      setIsDirty(false);
    }
    
    setIsOpen(false);
  };

  const handleInputChange = (key: string, value: string) => {
    setFormValues(prev => ({...prev, [key]: value}));
    setIsDirty(true);
    
    // 如果更改了适配器类型，则更新状态
    if (key === 'adapterType') {
      setAdapterType(value);
    }
  };

  return (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-2">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="text-sm text-gray-500 hover:text-gray-700 flex items-center"
        >
          <svg
            className="h-4 w-4 mr-1"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          设置
        </button>
        
        {/* 主题切换按钮 */}
        <button
          onClick={toggleTheme}
          className="text-sm bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded-md flex items-center"
        >
          {theme === 'light' ? (
            <>
              <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
              </svg>
              暗色模式
            </>
          ) : (
            <>
              <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
              </svg>
              亮色模式
            </>
          )}
        </button>
      </div>

      {isOpen && (
        <div className="mt-4 p-4 bg-white shadow-lg rounded-lg border animate-fadeIn">
          <h3 className="text-lg font-medium text-gray-900 mb-4">API设置</h3>
          
          {error && error.code === 'STORAGE_ERROR' && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4 rounded">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">
                    {error.message}: {error.details}
                  </p>
                </div>
                <div className="ml-auto pl-3">
                  <div className="-mx-1.5 -my-1.5">
                    <button
                      onClick={clearError}
                      className="inline-flex rounded-md p-1.5 text-red-500 hover:bg-red-100"
                    >
                      <span className="sr-only">关闭</span>
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {formFields.map((field) => {
              if (field.key === 'model') {
                return (
                  <div key={field.key} className="transition-all duration-200 ease-in-out">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label}
                    </label>
                    
                    {/* 自定义模型选择 */}
                    <div>
                      <div className="flex mb-2">
                        <input
                          type="text"
                          value={newModelName}
                          onChange={(e) => setNewModelName(e.target.value)}
                          placeholder="输入模型名称"
                          className="flex-grow mr-2 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
                        />
                        <button
                          type="button"
                          onClick={addCustomModel}
                          className="px-3 py-1 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700 transition-colors"
                        >
                          添加
                        </button>
                      </div>
                      
                      {customModels.length > 0 ? (
                        <div className="mb-2">
                          <select
                            value={formValues[field.key as keyof typeof formValues] || ''}
                            onChange={(e) => handleInputChange(field.key, e.target.value)}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border bg-white"
                          >
                            {customModels.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500 mb-2">
                          请添加至少一个自定义模型
                        </div>
                      )}
                      
                      {/* 已添加的模型列表 */}
                      <div className="mt-2">
                        <div className="text-sm font-medium text-gray-700 mb-1">已添加的模型:</div>
                        <div className="flex flex-wrap gap-2">
                          {customModels.map(model => (
                            <div 
                              key={model.value} 
                              className="flex items-center bg-gray-100 rounded-md px-2 py-1"
                            >
                              <span className="text-sm mr-1">{model.label}</span>
                              <button
                                type="button"
                                onClick={() => removeCustomModel(model.value)}
                                className="text-red-500 hover:text-red-700 ml-1"
                              >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
              
              return (
                <div key={field.key} className="transition-all duration-200 ease-in-out">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.label}
                  </label>
                  
                  {field.type === 'select' ? (
                    <select
                      value={formValues[field.key as keyof typeof formValues] || ''}
                      onChange={(e) => handleInputChange(field.key, e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border bg-white"
                    >
                      {field.options?.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : field.type === 'textarea' ? (
                    <textarea
                      value={formValues[field.key as keyof typeof formValues] || ''}
                      onChange={(e) => handleInputChange(field.key, e.target.value)}
                      rows={field.rows || 4}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
                      placeholder={field.placeholder}
                    />
                  ) : (
                    <input
                      type={field.type}
                      value={formValues[field.key as keyof typeof formValues] || ''}
                      onChange={(e) => handleInputChange(field.key, e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
                      placeholder={field.placeholder}
                    />
                  )}
                </div>
              );
            })}

            <div className="flex justify-between pt-4 mt-6 border-t">
              <button
                type="button"
                onClick={() => {
                  // 重置为默认提示词
                  if (window.confirm('确定要重置系统提示词为默认值吗？这将覆盖当前的自定义提示词。')) {
                    const defaultPrompt = `你是一个专业的决策分析助手。请根据用户的问题，分析不同的可能决策选项，并为每个选项评估成功概率。

严格遵循以下格式来响应，不要更改格式：

**选项 1: [决策标题]** (成功概率: XX%)
[详细描述选项1的利弊和原因]

**选项 2: [决策标题]** (成功概率: XX%)
[详细描述选项2的利弊和原因]

**选项 3: [决策标题]** (成功概率: XX%)
[详细描述选项3的利弊和原因]

格式要求:
1. 每个选项都必须以"**选项 序号: 标题**"开头，紧跟"(成功概率: XX%)"
2. 选项标题不要包含星号或其他特殊符号
3. 每个选项必须分配一个0-100之间的成功概率百分比
4. 每个选项标题后必须提供详细分析，描述该选项的利弊
5. 所有内容必须使用中文回答`;
                    
                    setFormValues(prev => ({...prev, prompt: defaultPrompt}));
                    setIsDirty(true);
                  }
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                重置提示词
              </button>
              
              <div>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="mr-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  type="submit"
                  className={`px-4 py-2 rounded-md text-white transition-colors ${
                    isDirty && (customModels.length > 0 || formValues.model)
                      ? 'bg-indigo-600 hover:bg-indigo-700'
                      : 'bg-gray-400 cursor-not-allowed'
                  }`}
                  disabled={!isDirty || (customModels.length === 0 && !formValues.model)}
                >
                  保存
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default SettingsPanel; 